'use client';

import Image from 'next/image';

import { useLanguage } from '@/context/LanguageContext';
import {
  ORG_CITY_STATE_ZIP,
  ORG_CONTACT_EMAIL,
  ORG_EIN,
  ORG_NAME,
  ORG_PO_BOX,
} from '@/lib/org-contact';

// The organization's identity comes from `@/lib/org-contact` so the footer and
// the Donate page cannot drift apart. The attribution below is footer-only, so
// it stays here. Values come from the approved Figma design for MWBI-901.
const BUILDER_NAME = 'Hill Town Platforms';
const BUILDER_URL = 'https://hilltownplatforms.com/';

const LINK_CLASSES = 'underline underline-offset-4 hover:no-underline';

/**
 * Site-wide page footer (MWBI-901).
 *
 * Rendered once per page from the shell -- inside the content column by
 * `NavBar`, and directly by `NavBarGuard` on the two routes that hide the
 * chrome. It is informational, not navigational: per the approved
 * requirements it is explicitly not a link menu or site map, so it uses no
 * `<nav>` element (a second `navigation` landmark would also collide with the
 * sidebar queries in NavBar.test.tsx).
 */
export function Footer() {
  const { t } = useLanguage();

  return (
    <footer
      data-testid="site-footer"
      // The global focus ring is `2px solid var(--primary)`, which is invisible
      // against bg-primary. Same white-ring fix as GreenCTASection.
      className="bg-primary text-background [&_a:focus-visible]:outline-white"
    >
      {/* The three blocks go side by side at lg, NOT md. The footer lives in the
          content column, and the 300px sidebar appears at exactly md -- so
          md:flex-row leaves roughly 470px for three columns. Measured: at a
          768px viewport that pushes document scrollWidth to 862px, i.e. the
          whole page scrolls sideways. At lg the column has room. */}
      {/* Vertical padding is deliberately asymmetric: the three blocks are
          top-aligned, and the logo is the tallest of them, so a symmetric `py`
          leaves more dead space under the text than above it. The bottom is
          therefore trimmed harder than the top. On the stacked layout the
          between-block gap is the other big term, so it tightens too, while the
          horizontal lg:gap-10 that separates the three columns is untouched.
          Measured: the band went 236px -> 212px -> 188px at 1440px, and
          443px -> 427px -> 387px at 375px, with the content never moving up. */}
      <div
        data-testid="site-footer-inner"
        className="px-6 pt-6 pb-4 lg:px-10 lg:pt-8 lg:pb-4 flex flex-col items-center gap-6 text-center lg:flex-row lg:items-start lg:justify-between lg:gap-10 lg:text-left"
      >
        {/* unoptimized: this repo has no `sharp` installed, so Next's built-in
            fallback resizer re-encodes optimized images at a visibly lower
            quality than the source (confirmed: a 104KB hand-resized PNG came
            back as a ~6-10KB re-compression). Every other image in the app
            already opts out of Next's optimizer for the same reason -- see
            `unoptimized` usage across frontend/src. This asset is already
            sized and compressed for its display box, so serving it as-is is
            both sharper and no more data than the optimizer would send. */}
        <Image
          src="/welcome-hero-logo.png"
          alt={t.footer.logoAlt}
          width={184}
          height={140}
          unoptimized
          className="h-auto w-[184px]"
          data-testid="site-footer-logo"
        />

        {/* Every line here is one atomic value -- an EIN, a PO box, a
            city/state/ZIP -- so none of them may wrap. Measured: without this,
            Spanish at a 1024px viewport breaks "EIN: 39-3429253" after the
            hyphen and renders it as two lines. They are short enough that
            nowrap never overflows, even stacked at 320px. */}
        <address
          data-testid="site-footer-address"
          className="not-italic text-sm lg:text-base leading-relaxed"
        >
          <span className="block whitespace-nowrap" data-testid="site-footer-org-name">{ORG_NAME}</span>
          <span className="block whitespace-nowrap" data-testid="site-footer-ein">{ORG_EIN}</span>
          <span className="block whitespace-nowrap" data-testid="site-footer-po-box">{ORG_PO_BOX}</span>
          <span className="block whitespace-nowrap" data-testid="site-footer-city">{ORG_CITY_STATE_ZIP}</span>
        </address>

        <div className="text-sm lg:text-base leading-relaxed">
          <p data-testid="site-footer-contact">
            {t.footer.contactLabel}{' '}
            <a href={`mailto:${ORG_CONTACT_EMAIL}`} className={LINK_CLASSES}>
              {ORG_CONTACT_EMAIL}
            </a>
          </p>
          <p data-testid="site-footer-built-by">
            {t.footer.builtByLabel}{' '}
            <a
              href={BUILDER_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={LINK_CLASSES}
            >
              {BUILDER_NAME}
            </a>
          </p>
          {/* The year is read from the clock so it never needs a yearly edit.
              suppressHydrationWarning covers the narrow window where a UTC
              server and a browser in a behind-UTC timezone straddle New Year. */}
          <p data-testid="site-footer-copyright" suppressHydrationWarning>
            {t.footer.copyright} {new Date().getFullYear()} {ORG_NAME}
          </p>
        </div>
      </div>
    </footer>
  );
}
